import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  LineChart,
  Package,
  Plus,
  Tags,
  Undo2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HasAccess } from "@/components/common/has-access";
import { bundleStatusToVariant } from "@/helpers/bundle-status-to-variant";
import BundleStandardGroup from "./bundle-standard-group";
import BulkPriceChangeDialog, {
  BulkMode,
} from "./bulk-price-change-dialog";
import {
  BundleProductGroupData,
  GroupedBundleLeaf,
} from "@/pages/bundles/types/grouped-bundle";

type BundleProductGroupProps = {
  group: BundleProductGroupData;
  onBundleClick: (bundle: GroupedBundleLeaf) => void;
};

export default function BundleProductGroup({
  group,
  onBundleClick,
}: BundleProductGroupProps) {
  // Collapsed by default — the user expands a product group to see its bundles.
  const [open, setOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState<BulkMode | null>(null);
  const navigate = useNavigate();
  const { region } = useParams();

  const offeringCount = group.offerings.length;
  const activeCount = group.offerings.filter((o) => o.current).length;
  const rollbackableCount = group.offerings.filter(
    (o) => o.current && o.history.length > 0,
  ).length;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/60">
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-3 text-left min-w-0 flex-1"
        >
          {open ? (
            <ChevronDown className="h-5 w-5 text-gray-500 shrink-0" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-500 shrink-0" />
          )}
          <Package className="h-4 w-4 text-gray-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-base font-semibold text-gray-900 truncate">
              {group.product?.name ?? "No Product"}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {group.product
                ? [group.product.category, group.product.type]
                    .filter(Boolean)
                    .join(" · ")
                : "bundles without a product"}
            </p>
          </div>
          {group.product && (
            <Badge
              variant={bundleStatusToVariant(group.product.status)}
              className="text-[10px] ml-1"
            >
              {group.product.status}
            </Badge>
          )}
        </button>

        <span className="text-xs text-gray-500 whitespace-nowrap">
          {offeringCount} offering{offeringCount === 1 ? "" : "s"} ·{" "}
          {activeCount} active
        </span>

        {/* Product-wide pricing timeline (all offerings) — visible to all. */}
        {group.product && (
          <Button
            size="sm"
            variant="ghost"
            className="shrink-0"
            title="View pricing timeline for all offerings"
            onClick={() =>
              navigate(
                `/${region ?? "all"}/pricing?product_id=${group.product!.id}`,
              )
            }
          >
            <LineChart className="h-4 w-4 mr-1" /> Timeline
          </Button>
        )}

        {group.product && (
          <HasAccess permission="write">
            <div className="flex items-center gap-2 shrink-0">
              {activeCount > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setBulkMode("change")}
                >
                  <Tags className="h-4 w-4 mr-1" /> Change all prices
                </Button>
              )}
              {rollbackableCount > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setBulkMode("rollback")}
                >
                  <Undo2 className="h-4 w-4 mr-1" /> Roll back all prices
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  navigate(
                    `/${region ?? "all"}/bundles/new?product_id=${group.product!.id}`,
                  )
                }
              >
                <Plus className="h-4 w-4 mr-1" /> Add Bundle
              </Button>
            </div>
          </HasAccess>
        )}
      </div>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {group.offerings.map((offering) => (
            <BundleStandardGroup
              key={offering.standard_id}
              offering={offering}
              onBundleClick={onBundleClick}
            />
          ))}
        </div>
      )}

      {bulkMode && group.product && (
        <BulkPriceChangeDialog
          open
          variant={bulkMode}
          productName={group.product.name}
          offerings={group.offerings}
          onClose={() => setBulkMode(null)}
        />
      )}
    </div>
  );
}
